import React, { type FC, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Image from '@atlaskit/image';
import type { ButtonProps } from '@atlaskit/button';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/core/close';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { Attribution, withErrorBoundary } from '@confluence/error-boundary';
import {
	WHITEBOARD_PAYWALL_UPSELL_ACTION_SUBJECT_ID,
	WHITEBOARD_PAYWALL_INFO_ACTION_SUBJECT_ID,
} from '@confluence/whiteboard-utils';

import type { LinkProps } from '../SectionMessage/ChangeEditionSectionMessageButtonsWrapper';
import { Card, CardContainer } from '../UpsellLooknFeel/Card';
import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery';
import { calculateTestId } from '../Upgrade/calculateTestId';
import { AnalyticsProvider, useEditionsAnalyticsContext } from '../Upgrade/AnalyticsProvider';

/**
 * A entitlement feature, as mentioned in {@link https://hello.atlassian.net/wiki/spaces/COMMERCE/pages/366025943/Adding+a+new+feature}
 * for whiteboards.
 *
 * This value can be found by looking through:
 * - ENTS: {@link https://stash.atlassian.com/projects/PUR/repos/entitlement-template/browse/entitlement-template-server/src/main/resources/entitlement-templates/data/1dd0e658-259d-4da8-beb9-eb0ebff86b96.v11.json}
 * - ConfluenceTransformer's ["features"]["whiteboards"] field in TCS: {@link https://snoopr.staging.atl-paas.net/ts?zone=staging&entity_type=CLOUD&key-type=AUTO_GUESS&s=pug.jira-dev.com#DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5-confluence}
 */
export type FeatureKey = string;

export type WhiteboardUpsellLinkProps = Omit<LinkProps, 'actionSubjectId' | 'messageDescriptor'> & {
	/**
	 * @privateRemarks i18n aware strings will be passed in from the Whiteboard's iframe
	 */
	message: string;
};

export type ImageSrcProps = {
	default: string;
	dark: string;
};

export type ImageProps = {
	src: ImageSrcProps;
	alt: string;
};

const i18n = defineMessages({
	cardDismissButton: {
		id: 'change-edition.card.dismiss-modal-btn',
		defaultMessage: 'Close modal',
		description:
			'Screen reader label for the cross-shaped icon (X) we show for users to click to close the modal',
	},
});

export type CardDismissButtonProps = {
	onClick: () => void;
	actionSubjectId: string;
	attributes?: Record<string, any>;
	source: string;
} & Pick<ButtonProps, 'testId'>;
export const CardDialogDismissButton = ({
	onClick,
	actionSubjectId,
	source,
	attributes,
	...otherButtonProps
}: CardDismissButtonProps) => {
	const intl = useIntl();

	const { wrapAnalyticsEventPayload } = useEditionsAnalyticsContext();

	const wrappedOnClick = useCallback(
		(_: any, analyticsEvent: UIAnalyticsEvent) => {
			analyticsEvent
				.update((payload) =>
					wrapAnalyticsEventPayload({
						type: 'sendUIEvent',
						data: {
							action: payload.action,
							actionSubject: payload.actionSubject,
							actionSubjectId,
							source,
							attributes: {
								...payload?.attributes,
								...attributes,
							},
						},
					}),
				)
				.fire();

			onClick();
		},
		[onClick, actionSubjectId, source, attributes, wrapAnalyticsEventPayload],
	);

	return (
		<Button
			appearance="link"
			onClick={wrappedOnClick}
			spacing="none"
			iconBefore={
				<CrossIcon label={intl.formatMessage(i18n.cardDismissButton)} color={token('color.icon')} />
			}
			testId={calculateTestId({
				actionSubjectId,
				source,
			})}
			{...otherButtonProps}
		/>
	);
};

export const WHITEBOARDS_UPSELL_SCREEN = 'whiteboardsUpsellCtaScreen';

/**
 * @privateRemarks Unlike other features, the whiteboards product communicates rendering of the upsell modal through an iframe
 * code for triggering lives in: https://bitbucket.org/atlassian/canvas
 */
export type WhiteboardUpsellProps = {
	/**
	 * @privateRemarks i18n aware strings will be passed in from the Whiteboard's iframe
	 */
	title: string;
	/**
	 * @privateRemarks i18n aware strings will be passed in from the Whiteboard's iframe
	 */
	body: string;
	feature: FeatureKey;
	image: ImageProps;
	learnMoreLink: WhiteboardUpsellLinkProps;
	onDismissRequest: () => void;
};
const WhiteboardUpsellComponent: FC<WhiteboardUpsellProps> = ({
	title,
	body,
	image,
	learnMoreLink,
	feature,
	onDismissRequest,
}) => {
	return (
		<AnalyticsProvider
			customAttributes={{
				feature,
			}}
		>
			<CardContainer>
				<Card
					withTasteAndToneStyling
					title={title}
					body={body}
					learnMoreLink={{
						...learnMoreLink,
						messageDescriptor: {
							id: 'change-edition.whiteboards.learn-more',
							defaultMessage: learnMoreLink.message,
						},
						actionSubjectId: WHITEBOARD_PAYWALL_INFO_ACTION_SUBJECT_ID,
					}}
					source={WHITEBOARDS_UPSELL_SCREEN}
					targetEdition={ConfluenceEdition.PREMIUM}
					upgradeEditionActionSubjectId={WHITEBOARD_PAYWALL_UPSELL_ACTION_SUBJECT_ID}
					buttonGroupAlignment="right"
					headerButton={
						<CardDialogDismissButton
							onClick={onDismissRequest}
							source={WHITEBOARDS_UPSELL_SCREEN}
							actionSubjectId="dismissButton"
						/>
					}
				>
					<Image
						src={image.src.default}
						srcDark={image.src.dark}
						alt={image.alt}
						width="100%"
						testId={calculateTestId({
							actionSubjectId: 'image',
							source: WHITEBOARDS_UPSELL_SCREEN,
						})}
					/>
				</Card>
			</CardContainer>
		</AnalyticsProvider>
	);
};

export const WhiteboardUpsell = withErrorBoundary({
	attribution: Attribution.WHITEBOARDS,
})(WhiteboardUpsellComponent);
